import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Download, PlusCircle } from "lucide-react";
import { Button } from "src/components/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "src/components/ui/Card";
import FileUpload from "src/components/ui/FileUpload";
import { Input } from "src/components/ui/Input";
import { Label } from "src/components/ui/Label";
import { Textarea } from "src/components/ui/TextArea";
import { useToast } from "src/hooks/use-toast";
import {
  EnrollmentBatchCapacity,
  manualEnrollmentService,
  SessionExtensionRecord,
} from "src/services/manualEnrollmentService";

interface AdminSessionExtensionFormProps {
  subscriptionId: string;
  studentId: string;
  currency: string;
  defaultPricePerSession: number | null;
  onExtended: () => Promise<void>;
}

const AdminSessionExtensionForm: React.FC<AdminSessionExtensionFormProps> = ({
  subscriptionId,
  studentId,
  currency,
  defaultPricePerSession,
  onExtended,
}) => {
  const { toast } = useToast();
  const [batch, setBatch] = useState<EnrollmentBatchCapacity | null>(null);
  const [history, setHistory] = useState<SessionExtensionRecord[]>([]);
  const [additionalSessions, setAdditionalSessions] = useState("");
  const [pricePerSession, setPricePerSession] = useState(
    defaultPricePerSession == null ? "" : String(defaultPricePerSession)
  );
  const [amountReceived, setAmountReceived] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [paidAt, setPaidAt] = useState("");
  const [verificationNotes, setVerificationNotes] = useState("");
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const extensionAmount = useMemo(
    () => Number(additionalSessions) * Number(pricePerSession),
    [additionalSessions, pricePerSession]
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const activeBatch = await manualEnrollmentService.fetchBatchCapacity(subscriptionId);
      setBatch(activeBatch);
      if (activeBatch) {
        const [extensions, extensionContext] = await Promise.all([
          manualEnrollmentService.listSessionExtensions(activeBatch.id),
          manualEnrollmentService.fetchBatchExtensionContext(activeBatch.id),
        ]);
        setHistory(extensions);
        if (extensionContext.actual_price_per_session != null) {
          setPricePerSession(String(extensionContext.actual_price_per_session));
        }
      } else {
        setHistory([]);
      }
    } catch (error) {
      toast({
        title: "Unable to load session extensions",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [subscriptionId, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const selectFile = (file: File) => {
    const upload = file as File & { error?: boolean; errorMessage?: string };
    if (upload.error) {
      toast({
        title: "Invalid invoice",
        description: upload.errorMessage || "Upload a PDF smaller than 10 MB.",
        variant: "destructive",
      });
      return;
    }
    setInvoiceFile(file);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const count = Number(additionalSessions);
    const rate = Number(pricePerSession);
    const received = Number(amountReceived);

    if (!batch || !invoiceFile || !paymentMethod.trim() || !paymentReference.trim() || !paidAt || !verificationNotes.trim()) {
      toast({ title: "Complete all extension details", description: "Payment verification and an invoice PDF are required.", variant: "destructive" });
      return;
    }
    if (!Number.isInteger(count) || count < 1 || !Number.isFinite(rate) || rate <= 0) {
      toast({ title: "Check session pricing", description: "Enter a positive whole session count and price per session.", variant: "destructive" });
      return;
    }
    if (!Number.isFinite(received) || received <= 0 || received > extensionAmount) {
      toast({ title: "Check payment received", description: "The received amount must be positive and cannot exceed the extension total.", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    try {
      await manualEnrollmentService.createAdminSessionExtension({
        batchId: batch.id,
        studentId,
        additionalSessions: count,
        actualPricePerSession: rate,
        amountReceived: received,
        currency,
        paymentMethod: paymentMethod.trim(),
        paymentReference: paymentReference.trim(),
        paidAt: new Date(paidAt).toISOString(),
        verificationNotes: verificationNotes.trim(),
        invoiceFile,
      });
      toast({ title: "Sessions added", description: `${count} sessions were added to ${batch.name}.` });
      setAdditionalSessions("");
      setAmountReceived("");
      setPaymentMethod("");
      setPaymentReference("");
      setPaidAt("");
      setVerificationNotes("");
      setInvoiceFile(null);
      await Promise.all([load(), onExtended()]);
    } catch (error) {
      toast({
        title: "Unable to add sessions",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) return <p className="text-sm text-gray-500">Loading active batch…</p>;
  if (!batch) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Add sessions</CardTitle>
        <CardDescription>
          Add paid sessions to {batch.name}. Current capacity: {batch.remaining_sessions}/{batch.total_sessions} remaining.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <form onSubmit={submit} className="grid gap-4 md:grid-cols-2">
          <div>
            <Label htmlFor="extension-session-count">Additional sessions</Label>
            <Input id="extension-session-count" type="number" min="1" step="1" value={additionalSessions} onChange={(event) => setAdditionalSessions(event.target.value)} required />
          </div>
          <div>
            <Label htmlFor="extension-rate">Price per session</Label>
            <Input id="extension-rate" type="number" min="0.01" step="0.01" value={pricePerSession} onChange={(event) => setPricePerSession(event.target.value)} required />
          </div>
          <div>
            <Label>Extension total</Label>
            <Input value={Number.isFinite(extensionAmount) ? `${currency.toUpperCase()} ${extensionAmount.toFixed(2)}` : ""} readOnly />
          </div>
          <div>
            <Label htmlFor="extension-received">Amount received</Label>
            <Input id="extension-received" type="number" min="0.01" step="0.01" max={extensionAmount || undefined} value={amountReceived} onChange={(event) => setAmountReceived(event.target.value)} required />
          </div>
          <div>
            <Label htmlFor="extension-method">Payment method</Label>
            <Input id="extension-method" value={paymentMethod} onChange={(event) => setPaymentMethod(event.target.value)} placeholder="Bank transfer, cash, cheque…" required />
          </div>
          <div>
            <Label htmlFor="extension-reference">Payment reference</Label>
            <Input id="extension-reference" value={paymentReference} onChange={(event) => setPaymentReference(event.target.value)} required />
          </div>
          <div>
            <Label htmlFor="extension-paid-at">Paid at</Label>
            <Input id="extension-paid-at" type="datetime-local" value={paidAt} onChange={(event) => setPaidAt(event.target.value)} required />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="extension-notes">Verification notes</Label>
            <Textarea id="extension-notes" value={verificationNotes} onChange={(event) => setVerificationNotes(event.target.value)} required />
          </div>
          <div className="md:col-span-2">
            <Label>Invoice PDF</Label>
            <FileUpload onFileSelect={selectFile} onFileRemove={() => setInvoiceFile(null)} fileName={invoiceFile?.name} accept=".pdf,application/pdf" maxSize={10 * 1024 * 1024} buttonText="Select extension invoice" required />
          </div>
          <div className="md:col-span-2">
            <Button type="submit" disabled={isSubmitting}>
              <PlusCircle className="mr-1 h-4 w-4" />
              {isSubmitting ? "Adding sessions…" : "Verify payment and add sessions"}
            </Button>
          </div>
        </form>

        {history.length > 0 && (
          <div className="border-t pt-4">
            <h3 className="mb-3 font-medium">Extension history</h3>
            <div className="space-y-2">
              {history.map((extension) => (
                <div key={extension.id} className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3 text-sm">
                  <span>
                    +{extension.additional_sessions} sessions · {extension.currency.toUpperCase()} {Number(extension.extension_amount).toFixed(2)} · {new Date(extension.created_at).toLocaleDateString()}
                  </span>
                  <Button type="button" size="sm" variant="outline" onClick={() => void manualEnrollmentService.createInvoiceDownloadUrl(extension.invoice_pdf_url).then((url) => window.open(url, "_blank", "noopener,noreferrer"))}>
                    <Download className="mr-1 h-4 w-4" />Invoice
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminSessionExtensionForm;
